import React, { useState, useEffect, useCallback } from 'react';
import {
    Container, Row, Col, Card, Badge, Table, Spinner, Alert, Tabs, Tab, Button
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine, faArrowDown, faArrowUp, faSync,
    faDatabase, faCalendarAlt, faTags, faBoxOpen
} from '@fortawesome/free-solid-svg-icons';
import {
    Chart as ChartJS, CategoryScale, LinearScale, BarElement,
    Title, Tooltip, Legend, PointElement, LineElement
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import api from '../api';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend);

const COND_LABEL = { '11': 'MC01 Nuovo', '2': 'MC02 Usato+', '3': 'MC03 Usato', '4': 'MC03 Usato' };
const COND_VARIANT = { '11': 'primary', '2': 'success', '3': 'secondary', '4': 'secondary' };

function StatCard({ icon, label, value, sub, color = 'primary' }) {
    return (
        <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="d-flex align-items-center gap-3">
                <div
                    className={`rounded-3 d-flex align-items-center justify-content-center text-${color}`}
                    style={{ width: 52, height: 52, background: `var(--bs-${color}-bg-subtle, #f0f4ff)`, flexShrink: 0 }}
                >
                    <FontAwesomeIcon icon={icon} size="lg" />
                </div>
                <div>
                    <div className="text-muted" style={{ fontSize: '0.78rem' }}>{label}</div>
                    <div className="fw-bold fs-4 lh-1">{value ?? '—'}</div>
                    {sub && <div className="text-muted" style={{ fontSize: '0.75rem' }}>{sub}</div>}
                </div>
            </Card.Body>
        </Card>
    );
}

function MoversTable({ rows, type }) {
    if (rows.length === 0) {
        return (
            <Alert variant="info" className="mt-3">
                Nessuna variazione rilevata. Sono necessarie almeno 2 run di repricing
                per calcolare i movimenti di prezzo.
            </Alert>
        );
    }
    return (
        <Table hover size="sm" className="mt-2" style={{ fontSize: '0.83rem' }}>
            <thead>
                <tr>
                    <th>Titolo</th>
                    <th>Condizione</th>
                    <th className="text-end">Prec.</th>
                    <th className="text-end">Attuale</th>
                    <th className="text-end">Δ €</th>
                    <th className="text-end">Δ %</th>
                    <th>Rilevato il</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((r, i) => (
                    <tr key={i}>
                        <td style={{ maxWidth: 280 }}>
                            <span title={r.titolo} className="text-truncate d-inline-block" style={{ maxWidth: 270 }}>
                                {r.titolo}
                            </span>
                        </td>
                        <td>
                            <Badge bg={COND_VARIANT[r.state_code] || 'secondary'} style={{ fontSize: '0.7rem' }}>
                                {COND_LABEL[r.state_code] || r.state_code}
                            </Badge>
                        </td>
                        <td className="text-end text-muted">{r.price_previous?.toFixed(2)}€</td>
                        <td className="text-end fw-semibold">{r.price_current?.toFixed(2)}€</td>
                        <td className={`text-end fw-bold ${type === 'drop' ? 'text-danger' : 'text-success'}`}>
                            {r.variation > 0 ? '+' : ''}{r.variation?.toFixed(2)}€
                        </td>
                        <td className={`text-end fw-bold ${type === 'drop' ? 'text-danger' : 'text-success'}`}>
                            {r.variation_pct > 0 ? '+' : ''}{r.variation_pct?.toFixed(1)}%
                        </td>
                        <td className="text-muted">
                            {r.ts_current?.slice(0, 16).replace('T', ' ')}
                        </td>
                    </tr>
                ))}
            </tbody>
        </Table>
    );
}

const RepricingDashboardPage = () => {
    const [stats, setStats] = useState(null);
    const [movers, setMovers] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [sRes, mRes] = await Promise.all([
                api.get('/mirakl/repricing/history/stats'),
                api.get('/mirakl/repricing/history/movers', { params: { limit: 30 } }),
            ]);
            setStats(sRes.data);
            setMovers(mRes.data);
        } catch (e) {
            setError('Impossibile caricare i dati. Verifica che il backend sia attivo.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Calcola "run totali" contando i giorni distinti nella timeline
    const runDays = movers?.timeline?.length ?? 0;

    // Dati grafico bar: timeline up/down/stable
    const timelineChart = movers?.timeline
        ? {
            labels: movers.timeline.map(t => t.day),
            datasets: [
                {
                    label: 'Aumenti',
                    data: movers.timeline.map(t => t.up),
                    backgroundColor: 'rgba(25,135,84,0.75)',
                    borderRadius: 3,
                },
                {
                    label: 'Riduzioni',
                    data: movers.timeline.map(t => t.down),
                    backgroundColor: 'rgba(220,53,69,0.75)',
                    borderRadius: 3,
                },
                {
                    label: 'Stabili',
                    data: movers.timeline.map(t => t.stable),
                    backgroundColor: 'rgba(108,117,125,0.45)',
                    borderRadius: 3,
                },
            ],
        }
        : null;

    return (
        <Container fluid className="py-4">
            {/* Header */}
            <Row className="mb-3 align-items-center">
                <Col>
                    <h1 className="h3 mb-0">
                        <FontAwesomeIcon icon={faChartLine} className="me-2 text-primary" />
                        Dashboard Variazioni Prezzi
                    </h1>
                    <small className="text-muted">
                        Storico movimenti di mercato rilevati dalle run di repricing P11
                    </small>
                </Col>
                <Col xs="auto" className="d-flex gap-2">
                    <Button variant="outline-secondary" size="sm" onClick={load} disabled={loading}>
                        <FontAwesomeIcon icon={faSync} spin={loading} className="me-1" />
                        Aggiorna
                    </Button>
                    <Button as={Link} to="/integrazioni/ibs/repricing" variant="outline-primary" size="sm">
                        <FontAwesomeIcon icon={faTags} className="me-1" />
                        Vai al Repricing
                    </Button>
                </Col>
            </Row>

            {error && <Alert variant="danger">{error}</Alert>}

            {loading && !stats ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <div className="mt-2 text-muted">Caricamento storico...</div>
                </div>
            ) : (
                <>
                    {/* Stat cards */}
                    <Row className="g-3 mb-4">
                        <Col xs={6} md={3}>
                            <StatCard
                                icon={faDatabase}
                                label="EAN tracciati"
                                value={stats?.ean_count?.toLocaleString('it-IT') ?? '—'}
                                sub="prodotti nello storico"
                                color="primary"
                            />
                        </Col>
                        <Col xs={6} md={3}>
                            <StatCard
                                icon={faBoxOpen}
                                label="Rilevazioni totali"
                                value={stats?.total_points?.toLocaleString('it-IT') ?? '—'}
                                sub="punti prezzo salvati"
                                color="info"
                            />
                        </Col>
                        <Col xs={6} md={3}>
                            <StatCard
                                icon={faCalendarAlt}
                                label="Giorni con dati"
                                value={runDays}
                                sub={stats?.last_run ? `ultima: ${stats.last_run}` : 'nessuna run ancora'}
                                color="success"
                            />
                        </Col>
                        <Col xs={6} md={3}>
                            <StatCard
                                icon={faArrowDown}
                                label="Riduzioni ultima run"
                                value={movers?.total_drops ?? '—'}
                                sub={`${movers?.total_rises ?? '—'} aumenti`}
                                color="danger"
                            />
                        </Col>
                    </Row>

                    {/* Grafico timeline */}
                    {timelineChart && movers.timeline.length > 0 ? (
                        <Card className="mb-4 border-0 shadow-sm">
                            <Card.Header className="bg-white border-0 pt-3">
                                <strong>Andamento mercato nel tempo</strong>
                                <small className="text-muted ms-2">
                                    — quanti EAN sono aumentati / ridotti / stabili per ogni run
                                </small>
                            </Card.Header>
                            <Card.Body style={{ height: 260 }}>
                                <Bar
                                    data={timelineChart}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        plugins: {
                                            legend: { position: 'top', labels: { boxWidth: 12, padding: 16 } },
                                            tooltip: {
                                                callbacks: {
                                                    label: ctx => `${ctx.dataset.label}: ${ctx.parsed.y} EAN`,
                                                }
                                            }
                                        },
                                        scales: {
                                            x: { stacked: false, ticks: { maxTicksLimit: 14, maxRotation: 45 } },
                                            y: { beginAtZero: true, ticks: { precision: 0 } },
                                        }
                                    }}
                                />
                            </Card.Body>
                        </Card>
                    ) : !loading && (
                        <Alert variant="info" className="mb-4">
                            <FontAwesomeIcon icon={faChartLine} className="me-2" />
                            Nessun dato di timeline ancora. Esegui almeno 2 run di repricing consecutive per vedere le variazioni.
                        </Alert>
                    )}

                    {/* Top movers */}
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white border-0 pt-3">
                            <strong>Top movimenti di prezzo</strong>
                            <small className="text-muted ms-2">
                                — variazione tra ultima e penultima rilevazione (max 30 per tipo)
                            </small>
                        </Card.Header>
                        <Card.Body className="px-3 pt-1">
                            <Tabs defaultActiveKey="drops" className="mb-1">
                                <Tab
                                    eventKey="drops"
                                    title={
                                        <span>
                                            <FontAwesomeIcon icon={faArrowDown} className="me-1 text-danger" />
                                            Riduzioni mercato
                                            {movers?.total_drops > 0 && (
                                                <Badge bg="danger" className="ms-1" style={{ fontSize: '0.7rem' }}>
                                                    {movers.total_drops}
                                                </Badge>
                                            )}
                                        </span>
                                    }
                                >
                                    <MoversTable rows={movers?.drops ?? []} type="drop" />
                                </Tab>
                                <Tab
                                    eventKey="rises"
                                    title={
                                        <span>
                                            <FontAwesomeIcon icon={faArrowUp} className="me-1 text-success" />
                                            Aumenti mercato
                                            {movers?.total_rises > 0 && (
                                                <Badge bg="success" className="ms-1" style={{ fontSize: '0.7rem' }}>
                                                    {movers.total_rises}
                                                </Badge>
                                            )}
                                        </span>
                                    }
                                >
                                    <MoversTable rows={movers?.rises ?? []} type="rise" />
                                </Tab>
                            </Tabs>
                        </Card.Body>
                    </Card>
                </>
            )}
        </Container>
    );
};

export default RepricingDashboardPage;
